import { motion } from "framer-motion";
import { useLanguage, useSEO } from "../context/LanguageContext";
import { KineticLines, Reveal, Overline, SectionHeading, CTAButton } from "../components/Kinetic";
import { ArrowUpRight } from "lucide-react";

const ARCH_IMG =
  "https://images.unsplash.com/photo-1614595737476-42487331b8a1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBhcmNoaXRlY3R1cmUlMjBjbGVhbnxlbnwwfHx8fDE3ODgxMDYyOTJ8MA&ixlib=rb-4.1.0&q=85";

const CentrePage = () => {
  const { t } = useLanguage();
  useSEO(t("centre.metaTitle"), t("centre.metaDesc"));
  const vocations = t("centre.vocations");
  const axes = t("centre.axes");
  const spans = ["lg:col-span-2", "", "", "", "lg:col-span-2", "", "lg:col-span-2"];

  return (
    <div data-testid="centre-page">
      <header className="relative overflow-hidden bg-graphite">
        <motion.img
          src={ARCH_IMG}
          alt=""
          initial={{ scale: 1.12, opacity: 0 }}
          animate={{ scale: 1, opacity: 0.3 }}
          transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-graphite via-graphite/40 to-graphite/60" />
        <div className="relative mx-auto max-w-7xl px-6 pb-24 pt-44 md:pb-32 md:pt-56">
          <Reveal y={16}>
            <span className="inline-block border border-[#4ADE80]/50 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] text-[#4ADE80]" data-testid="centre-status-badge">
              {t("centre.overline")}
            </span>
          </Reveal>
          <h1 className="mt-8 font-display font-light text-5xl md:text-6xl lg:text-[5.5rem] tracking-tighter leading-[1.02] text-white" data-testid="centre-title">
            <KineticLines lines={t("centre.titleLines")} delay={0.25} />
          </h1>
          <Reveal delay={0.7}>
            <p className="mt-8 max-w-2xl font-display text-xl md:text-2xl font-light text-white/80">
              {t("centre.subtitle")}
            </p>
          </Reveal>
          <Reveal delay={0.85}>
            <p className="mt-6 max-w-2xl border-l-2 border-[#4ADE80] pl-5 text-sm md:text-base leading-relaxed text-white/60">
              {t("centre.disclaimer")}
            </p>
          </Reveal>
        </div>
      </header>

      <section className="py-24 md:py-32">
        <div className="mx-auto grid max-w-7xl gap-14 px-6 lg:grid-cols-2">
          <SectionHeading overline={t("centre.vocationOverline")} title={t("centre.vocationTitle")} />
          <ul>
            {vocations.map((v, i) => (
              <Reveal key={v} delay={i * 0.04} y={12}>
                <li className="group flex items-center justify-between gap-6 border-b border-graphite/10 py-4 transition-colors duration-200 hover:border-forest/40">
                  <span className="text-base text-graphite">{v}</span>
                  <ArrowUpRight className="h-4 w-4 shrink-0 text-graphite/30 transition-all duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-forest" />
                </li>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      <section className="bg-graphite py-24 text-white md:py-32">
        <div className="mx-auto max-w-7xl px-6">
          <SectionHeading overline={t("centreSteps.overline")} title={t("centreSteps.title")} text={t("centreSteps.note")} light />
          <div className="mt-16 grid gap-px bg-white/10 md:grid-cols-2 lg:grid-cols-5" data-testid="centre-steps">
            {t("centreSteps.steps").map((s, i) => (
              <Reveal key={s.num} delay={i * 0.07} className="group bg-graphite p-7 transition-colors duration-300 hover:bg-forest">
                <p className="font-mono text-sm text-[#4ADE80]">{s.num}</p>
                <h3 className="mt-4 font-display text-xl font-bold uppercase tracking-tight text-white">{s.title}</h3>
                <ul className="mt-4 space-y-2">
                  {s.items.map((it) => (
                    <li key={it} className="text-sm leading-relaxed text-white/60 transition-colors duration-300 group-hover:text-white/80">
                      — {it}
                    </li>
                  ))}
                </ul>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-graphite/10 bg-white py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-6">
          <SectionHeading overline={t("centre.axesOverline")} title={t("centre.axesTitle")} text={t("centre.axesNote")} />
          <div className="mt-16 grid grid-cols-1 gap-4 md:grid-cols-3 lg:grid-cols-4" data-testid="centre-axes-grid">
            {axes.map((a, i) => (
              <Reveal key={a.num} delay={i * 0.06} className={`group border border-graphite/10 bg-bone p-7 transition-colors duration-300 hover:bg-forest md:p-8 ${spans[i]}`}>
                <p className="font-mono text-sm text-forest transition-colors duration-300 group-hover:text-[#4ADE80]">{a.num}</p>
                <h3 className="mt-5 font-display text-xl md:text-2xl font-bold uppercase tracking-tight leading-tight text-graphite transition-colors duration-300 group-hover:text-white">
                  {a.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-graphite-soft transition-colors duration-300 group-hover:text-white/70">
                  {a.text}
                </p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-forest py-24 text-white md:py-32">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal>
            <h2 className="max-w-3xl font-display text-3xl md:text-5xl font-light tracking-tight leading-tight">
              {t("centre.ctaTitle")}
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-10 flex flex-wrap gap-4">
              <CTAButton to="/scientifiques" variant="light" testid="centre-cta-network">
                {t("centre.cta1")}
              </CTAButton>
              <CTAButton to="/feuille-de-route" variant="outlineLight" testid="centre-cta-roadmap">
                {t("centre.cta2")}
              </CTAButton>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default CentrePage;
